import * as React from 'react';

import Menu from '../components/Menu';
import SubMenu from '../components/SubMenu';
import MenuItem from '../components/MenuItem';
import { BACKGROUND_REPEAT_LABELS } from '../data/BackgroundRepeat';
import BlendMode, { BLEND_MODE_LABELS } from '../data/BlendMode';

import Layer from '../data/Layer';
import { getStoreState, storeDispatch } from '../state/Store';
import * as Actions from '../state/actions';
import { RemoveLayerCommand } from 'state/commands/RemoveLayerCommand';
import { BackgroundRepeat } from '@beatflyer/shaders-lib';
import { EditLayerRepeat } from 'state/commands/EditLayerRepeat';
import { EditLayerBlendModeCommand } from 'state/commands/EditLayerBlendModeCommand';
import { ToggleLayerVisibilityCommand } from 'state/commands/ToggleLayerVisibilityCommand';
import { ToggleLayerLockCommand } from 'state/commands/ToggleLayerLockCommand';
import { RemoveMaskCommand } from 'state/commands/RemoveMaskCommand';
import { ToggleLayerSyncPlaybackCommand } from 'state/commands/ToggleLayerSyncPlaybackCommand';
import { DuplicateLayerCommand } from 'state/commands/DuplicateLayerCommand';
import { FrameType } from 'data/FrameType';
import { useUser } from 'hooks/useUser';
import { isPro } from 'utils';
import { RemoveBackgroundCommand } from 'state/commands/RemoveBackgroundCommand';

interface Props {
  layer: Layer;
  hasMultipleFrames: boolean;
  getParent: () => HTMLElement;
  onClose: () => void;
  hasEffects: boolean;
}

export const LayerRowOptionsMenu: React.FC<Props> = props => {
  const { layer, onClose, hasMultipleFrames, getParent, hasEffects } = props;
  const [actualBlendMode, setActualBlendMode] = React.useState(
    props.layer.blendMode
  );
  const [previewedBlendMode, setPreviewerdBlendMode] = React.useState<
    BlendMode | undefined
  >();
  const user = useUser();

  const onClick = React.useCallback(
    key => {
      if (key === 'delete') {
        onClose();
        storeDispatch(new RemoveLayerCommand(layer.id));
      } else if (key.startsWith('repeat.')) {
        const repeat = parseInt(key.split('.')[1], 10) as BackgroundRepeat;
        storeDispatch(new EditLayerRepeat(layer.id, repeat));
      } else if (key.startsWith('blend.')) {
        setPreviewerdBlendMode(undefined);
        const blend = parseInt(key.split('.')[1], 10) as BlendMode;
        if (blend === actualBlendMode) {
          return;
        }
        storeDispatch(
          new EditLayerBlendModeCommand(layer.id, blend, actualBlendMode)
        );
        setActualBlendMode(blend);
      } else if (key === 'syncPlayback') {
        storeDispatch(new ToggleLayerSyncPlaybackCommand(layer.id));
      } else if (key === 'split') {
        onClose();
        storeDispatch(Actions.startLayerSplitting(layer));
      } else if (key === 'duplicate') {
        onClose();
        storeDispatch(new DuplicateLayerCommand(layer.id));
      } else if (key === 'view') {
        storeDispatch(new ToggleLayerVisibilityCommand(layer.id));
      } else if (key === 'lock') {
        storeDispatch(new ToggleLayerLockCommand(layer.id));
      } else if (key === 'removeMask') {
        storeDispatch(new RemoveMaskCommand(layer.id));
      } else if (key === 'addMask') {
        onClose();
        storeDispatch(Actions.showAddMaskModal(layer));
      } else if (key === 'moveMask') {
        onClose();
        storeDispatch(Actions.selectMask(layer));
        onClose();
      } else if (key === 'editMask') {
        storeDispatch(Actions.editShape(layer));
      } else if (key === 'removeBg') {
        onClose();
        if (!isPro(user)) {
          storeDispatch(Actions.showBuyingOptions());
        } else {
          storeDispatch(
            new RemoveBackgroundCommand(layer.id, layer.frames[0].id)
          );
        }
      }
    },
    [layer, onClose, actualBlendMode]
  );

  const onBlendHover = React.useCallback(
    (key: string) => {
      if (!key.startsWith('blend.')) {
        return;
      }
      const blend = parseInt(key.split('.')[1], 10) as BlendMode;
      setPreviewerdBlendMode(blend);
      storeDispatch({
        type: Actions.CHANGE_LAYER_BLEND,
        layer,
        blendMode: blend,
        ignoreChange: true
      });
    },
    [layer]
  );

  const onBlendSubMenuClose = React.useCallback(() => {
    if (
      previewedBlendMode !== undefined &&
      previewedBlendMode !== actualBlendMode
    ) {
      storeDispatch({
        type: Actions.CHANGE_LAYER_BLEND,
        layer,
        blendMode: actualBlendMode,
        ignoreChange: true
      });
    }
  }, [previewedBlendMode, actualBlendMode, layer]);

  const toggleVisibility = (
    <MenuItem actionKey="view" icon={layer.visible ? 'hide' : 'show'}>
      {layer.visible ? 'Hide' : 'Show'}
    </MenuItem>
  );

  if (!layer.visible) {
    return (
      <Menu
        getParent={getParent}
        onClose={onClose}
        onClick={onClick}
        onHover={onBlendHover}
      >
        {toggleVisibility}
      </Menu>
    );
  }

  const { isBeatflyerLite } = getStoreState().app;
  return (
    <Menu
      getParent={getParent}
      onClose={onClose}
      onClick={onClick}
      onHover={onBlendHover}
    >
      {!isBeatflyerLite &&
        (layer.mask !== null ? (
          <SubMenu title="Mask" icon="mask">
            <MenuItem actionKey="editMask">Edit shape</MenuItem>
            <MenuItem actionKey="moveMask">Move and resize</MenuItem>
            <MenuItem actionKey="removeMask" className="red">
              Remove mask
            </MenuItem>
          </SubMenu>
        ) : (
          <MenuItem actionKey="addMask" icon="mask">
            Add mask
          </MenuItem>
        ))}
      {!isBeatflyerLite &&
        !hasMultipleFrames &&
        layer.frames[0].type === FrameType.Image && (
          <MenuItem actionKey="removeBg" icon="remove-bg">
            Remove background
          </MenuItem>
        )}
      {hasMultipleFrames && (
        <MenuItem
          actionKey="syncPlayback"
          selected={layer.syncPlayback}
          icon={(layer.syncPlayback ? 'un' : '') + 'sync'}
        >
          {(layer.syncPlayback ? 'Uns' : 'S') + 'ync playback'}
        </MenuItem>
      )}
      <SubMenu
        title="Blend Mode"
        selectable={true}
        icon="layers"
        onClose={onBlendSubMenuClose}
      >
        {BLEND_MODE_LABELS.map((item, index) => {
          return (
            <MenuItem
              actionKey={'blend.' + index}
              key={index}
              selected={actualBlendMode === index}
            >
              {item}
            </MenuItem>
          );
        })}
      </SubMenu>
      {hasEffects && (
        <SubMenu title="Repeat texture" selectable={true} icon="repeat">
          {BACKGROUND_REPEAT_LABELS.map((item, index) => {
            return (
              <MenuItem
                actionKey={'repeat.' + index}
                key={index}
                selected={layer.repeat === index}
              >
                {item}
              </MenuItem>
            );
          })}
        </SubMenu>
      )}
      {!isBeatflyerLite && (
        <>
          <MenuItem actionKey="duplicate" icon="duplicate">
            Duplicate
          </MenuItem>
          <MenuItem actionKey="split" icon="split">
            Split...
          </MenuItem>
          {toggleVisibility}
          <MenuItem actionKey="lock" icon={layer.locked ? 'unlock' : 'lock'}>
            {layer.locked ? 'Unlock' : 'Lock'}
          </MenuItem>
          <MenuItem actionKey="delete" className="red" icon="bin">
            Remove
          </MenuItem>
        </>
      )}
    </Menu>
  );
};



// WEBPACK FOOTER //
// ./src/controller/LayerRowOptionsMenu.tsx